import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.i3');
import { OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Service } from "src/libs/portal/season/service";
import {
    cellyticsAnalysisDataUrl,
    cellyticsAnalysisSet,
    cellyticsCurrentAnalysisSetId,
    cellyticsDefaultAnalysisSet,
} from '../cellytics-analysis';

const ACCESS_SESSION_KEY = "cellytics.access";
const ACCESS_SESSION_VALUE = "granted";
const ACCESS_USER = {
    id: "cellytics-access",
    email: "",
    name: "Cellytics NK",
    role: "admin",
};

const TARGET_CASE_IDS_WITH_BACKGROUND = [
    "original",
    "background",
    "blur",
    "mmd_filter",
    "background_blur",
    "background_mmd_filter",
    "blur_mmd_filter",
    "background_blur_mmd_filter",
];
const TARGET_CASE_IDS_WITHOUT_BACKGROUND = ["original", "blur", "mmd_filter", "blur_mmd_filter"];

@Component({
    selector: 'wiz-page-i3',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.i3/view.scss */
:host {
  display: block;
  height: 100%;
}`],
})
export class PageI3Component implements OnInit, OnDestroy {
    public analysis: any = null;
    public analysisSetId: string = "";
    public analysisSet: any = null;
    public loading: boolean = true;
    public error: string = "";
    public selectedCaseId: string = "";
    public diagnosisFilter: string = "all";
    public i3Analysis: any = this.emptyI3Analysis();
    public diagnosisOptions: any[] = [
        { id: "all", label: "전체" },
        { id: "normal", label: "정상군" },
        { id: "patient", label: "환자군" },
    ];

    private routerSub: any = null;

    constructor(@Inject( Service) public service: Service,@Inject( Router)  private router: Router) { }

    public async ngOnInit() {
        await this.service.init();
        if (!this.hasAccess()) {
            location.href = "/access";
            return;
        }
        this.applyAccessSession();
        this.syncAnalysisSet();
        this.bindRouteChanges();
        await this.load();
    }

    public ngOnDestroy() {
        if (this.routerSub) this.routerSub.unsubscribe();
    }

    private hasAccess() {
        return this.service.auth.check() || window.sessionStorage.getItem(ACCESS_SESSION_KEY) === ACCESS_SESSION_VALUE;
    }

    private applyAccessSession() {
        if (this.service.auth.check()) return;
        this.service.auth.status = true;
        this.service.auth.session = ACCESS_USER;
    }

    private syncAnalysisSet() {
        this.analysisSetId = cellyticsCurrentAnalysisSetId();
        this.analysisSet = cellyticsAnalysisSet(this.analysisSetId) || cellyticsDefaultAnalysisSet();
    }

    private bindRouteChanges() {
        if (this.routerSub) return;
        this.routerSub = this.router.events.subscribe(async (event: any) => {
            if (!(event instanceof NavigationEnd)) return;
            const nextSetId = cellyticsCurrentAnalysisSetId();
            if (nextSetId === this.analysisSetId) return;
            this.syncAnalysisSet();
            await this.load();
        });
    }

    public async load() {
        this.syncAnalysisSet();
        this.loading = true;
        this.error = "";
        this.i3Analysis = this.emptyI3Analysis();
        await this.service.render();

        try {
            const response = await fetch(cellyticsAnalysisDataUrl(this.analysisSetId), { cache: "no-cache" });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            this.analysis = await response.json();
            this.i3Analysis = this.buildI3Analysis();
            this.selectedCaseId = this.defaultSelectedCaseId();
        } catch (error) {
            this.error = "분석 데이터를 불러오지 못했습니다.";
        }

        this.loading = false;
        await this.service.render();
    }

    public get threshold() {
        return this.analysis?.parameters?.mmdSdThreshold || 13.5;
    }

    public get selectedSummary() {
        return this.i3Analysis.summaries.find((row: any) => row.caseId === this.selectedCaseId) || this.i3Analysis.summaries[0] || null;
    }

    public get baselineSummary() {
        return this.i3Analysis.summaries.find((row: any) => row.caseId === "original") || null;
    }

    public get selectedGroupRows() {
        return this.i3Analysis.groups
            .map((group: any) => ({
                ...group,
                baseline: group.caseResultMap.original,
                selected: group.caseResultMap[this.selectedCaseId],
            }))
            .filter((row: any) => !!row.selected && row.selected.i3 !== null)
            .filter((row: any) => this.diagnosisFilter === "all" || row.diagnosis === this.diagnosisFilter);
    }

    public get requestScopeText() {
        if (this.analysisSetId === "260612") {
            return "260612는 autoled가 없어 Blur, MMD_STD 13.5, Blur+MMD_STD 조합으로 원본 대비 I3 변화를 비교합니다.";
        }
        return "260619는 밝기 보정, Blur, MMD_STD 13.5 단일 적용과 2개 조합, 전체 조합까지 원본 대비 I3 변화를 비교합니다.";
    }

    public get selectedCaseLabel() {
        return this.selectedSummary?.case?.label || "-";
    }

    public async selectCase(caseId: string) {
        this.selectedCaseId = caseId;
        await this.service.render();
    }

    public async selectDiagnosis(filterId: string) {
        this.diagnosisFilter = filterId;
        await this.service.render();
    }

    public caseButtonClass(caseId: string) {
        if (this.selectedCaseId === caseId) {
            return "rounded-lg border border-emerald-500 bg-emerald-50 px-3 py-2 text-left text-[12px] font-semibold text-emerald-800";
        }
        return "rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-[12px] font-medium text-gray-600 hover:border-gray-300 hover:bg-gray-50";
    }

    public diagnosisFilterClass(filterId: string) {
        if (this.diagnosisFilter === filterId) {
            return "rounded-md border border-emerald-500 bg-emerald-50 px-3 py-1.5 text-[12px] font-semibold text-emerald-800";
        }
        return "rounded-md border border-gray-200 bg-white px-3 py-1.5 text-[12px] font-medium text-gray-600 hover:border-gray-300 hover:bg-gray-50";
    }

    public diagnosisClass(diagnosis: string) {
        if (diagnosis === "normal") return "rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700";
        if (diagnosis === "patient") return "rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700";
        return "rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500";
    }

    public deltaClass(value: any) {
        if (value > 0) return "text-blue-700";
        if (value < 0) return "text-red-600";
        return "text-gray-500";
    }

    public signed(value: any, digits: number = 1) {
        if (value === null || value === undefined || Number.isNaN(Number(value))) return "-";
        const numberValue = Number(value);
        const prefix = numberValue > 0 ? "+" : "";
        return `${prefix}${this.format(numberValue, digits)}`;
    }

    public format(value: any, digits: number = 1) {
        if (value === null || value === undefined || Number.isNaN(Number(value))) return "-";
        return Number(value).toLocaleString("ko-KR", {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
        });
    }

    private emptyI3Analysis() {
        return {
            caseIds: [],
            summaries: [],
            groups: [],
            i3Chart: this.emptyChart(),
            deltaChart: this.emptyChart(),
            normalGroupCount: 0,
            patientGroupCount: 0,
        };
    }

    private emptyChart() {
        return {
            width: 720,
            height: 330,
            left: 68,
            right: 26,
            top: 28,
            bottom: 64,
            ticks: [],
            labels: [],
            bars: [],
            zeroY: 0,
        };
    }

    private defaultSelectedCaseId() {
        const preferred = this.analysis?.analysisSet?.backgroundAvailable === false
            ? "blur_mmd_filter"
            : "background_blur_mmd_filter";
        if (this.i3Analysis.summaries.some((row: any) => row.caseId === preferred)) return preferred;
        return this.i3Analysis.summaries[this.i3Analysis.summaries.length - 1]?.caseId || "";
    }

    private buildI3Analysis() {
        if (!this.analysis) return this.emptyI3Analysis();
        const caseIds = this.targetCaseIds();
        const groups = this.buildI3Groups(caseIds);
        const summaries = this.buildI3Summaries(groups, caseIds);
        return {
            caseIds,
            summaries,
            groups,
            i3Chart: this.buildI3Chart(summaries),
            deltaChart: this.buildDeltaChart(summaries),
            normalGroupCount: groups.filter((row: any) => row.diagnosis === "normal").length,
            patientGroupCount: groups.filter((row: any) => row.diagnosis === "patient").length,
        };
    }

    private targetCaseIds() {
        const source = this.analysis?.analysisSet?.backgroundAvailable === false
            ? TARGET_CASE_IDS_WITHOUT_BACKGROUND
            : TARGET_CASE_IDS_WITH_BACKGROUND;
        return source.filter((caseId: string) => !!this.findCase(caseId));
    }

    private findCase(caseId: string) {
        return (this.analysis?.cases || []).find((row: any) => row.id === caseId) || null;
    }

    private buildI3Groups(caseIds: string[]) {
        const groupMap: any = {};
        for (const sample of this.analysis?.samples || []) {
            const assayType = this.assayType(sample);
            if (!assayType) continue;
            const groupKey = this.groupKey(sample);
            if (!groupMap[groupKey]) {
                groupMap[groupKey] = {
                    groupKey,
                    groupLabel: this.groupLabel(sample),
                    subjectId: sample.subjectId || sample.sampleNumber || groupKey,
                    sampleNumber: sample.sampleNumber || sample.subjectLabel || sample.subjectId || groupKey,
                    diseaseName: sample.diseaseName || "",
                    replicate: sample.replicate || "",
                    diagnosis: sample.diagnosis || "",
                    diagnosisLabel: sample.diagnosisLabel || "-",
                    diagnosisOrder: sample.diagnosisOrder ?? 99,
                    buckets: this.emptyBuckets(caseIds),
                };
            }
            for (const caseId of caseIds) {
                const caseRow = this.sampleCase(sample, caseId);
                const csp = this.numberValue(caseRow?.metrics?.CSP);
                if (csp === null) continue;
                groupMap[groupKey].buckets[caseId][assayType].push(csp);
            }
        }

        const groups = Object.values(groupMap).map((group: any) => {
            const caseRows = caseIds.map((caseId: string) => this.buildGroupCaseResult(group, caseId));
            const baseline = caseRows.find((row: any) => row.caseId === "original") || null;
            const caseResultMap: any = {};
            for (const row of caseRows) {
                if (baseline?.i3 !== null && baseline?.i3 !== undefined && row.i3 !== null) {
                    row.delta = this.finite(row.i3 - baseline.i3);
                    row.deltaPercent = baseline.i3 ? this.finite((row.delta / baseline.i3) * 100) : null;
                }
                caseResultMap[row.caseId] = row;
            }
            return { ...group, caseRows, caseResultMap };
        }).filter((group: any) => group.caseResultMap.original?.i3 !== null);

        groups.sort((a: any, b: any) => {
            if (a.diagnosisOrder !== b.diagnosisOrder) return a.diagnosisOrder - b.diagnosisOrder;
            return String(a.groupLabel).localeCompare(String(b.groupLabel));
        });
        return groups;
    }

    private emptyBuckets(caseIds: string[]) {
        const buckets: any = {};
        for (const caseId of caseIds) {
            buckets[caseId] = { asc: [], vehicle: [] };
        }
        return buckets;
    }

    private buildGroupCaseResult(group: any, caseId: string) {
        const bucket = group.buckets[caseId] || { asc: [], vehicle: [] };
        const ascCsp = this.average(bucket.asc);
        const vehicleCsp = this.average(bucket.vehicle);
        const i3 = ascCsp !== null && vehicleCsp ? this.finite((ascCsp / vehicleCsp) * 100) : null;
        return {
            caseId,
            case: this.findCase(caseId),
            ascCsp,
            vehicleCsp,
            ascCount: bucket.asc.length,
            vehicleCount: bucket.vehicle.length,
            i3,
            delta: null,
            deltaPercent: null,
        };
    }

    private buildI3Summaries(groups: any[], caseIds: string[]) {
        return caseIds.map((caseId: string) => {
            const caseMeta = this.findCase(caseId);
            const normalRows = groups
                .filter((group: any) => group.diagnosis === "normal")
                .map((group: any) => group.caseResultMap[caseId])
                .filter((row: any) => !!row && row.i3 !== null);
            const patientRows = groups
                .filter((group: any) => group.diagnosis === "patient")
                .map((group: any) => group.caseResultMap[caseId])
                .filter((row: any) => !!row && row.i3 !== null);
            const allRows = groups
                .map((group: any) => group.caseResultMap[caseId])
                .filter((row: any) => !!row && row.i3 !== null);
            const normalAvgI3 = this.average(normalRows.map((row: any) => row.i3));
            const patientAvgI3 = this.average(patientRows.map((row: any) => row.i3));
            return {
                caseId,
                case: caseMeta,
                allAvgI3: this.average(allRows.map((row: any) => row.i3)),
                normalAvgI3,
                patientAvgI3,
                normalAvgDelta: this.average(normalRows.map((row: any) => row.delta)),
                patientAvgDelta: this.average(patientRows.map((row: any) => row.delta)),
                normalAvgDeltaPercent: this.average(normalRows.map((row: any) => row.deltaPercent)),
                patientAvgDeltaPercent: this.average(patientRows.map((row: any) => row.deltaPercent)),
                separation: normalAvgI3 !== null && patientAvgI3 !== null ? this.finite(patientAvgI3 - normalAvgI3) : null,
                groupCount: allRows.length,
                normalGroupCount: normalRows.length,
                patientGroupCount: patientRows.length,
            };
        });
    }

    private buildI3Chart(summaries: any[]) {
        const values = summaries.flatMap((row: any) => [row.normalAvgI3, row.patientAvgI3]).filter((value: any) => value !== null && value !== undefined);
        return this.buildGroupedBarChart(summaries, values, "i3");
    }

    private buildDeltaChart(summaries: any[]) {
        const values = summaries.flatMap((row: any) => [row.normalAvgDelta, row.patientAvgDelta]).filter((value: any) => value !== null && value !== undefined);
        return this.buildGroupedBarChart(summaries, values, "delta");
    }

    private buildGroupedBarChart(summaries: any[], values: any[], mode: string) {
        const chart = this.emptyChart();
        const domain = this.chartDomain(values, true);
        chart.ticks = this.buildTicks(domain.min, domain.max, 5).map((value: number) => ({
            value,
            y: this.scaleY(value, domain.min, domain.max, chart),
        }));
        chart.zeroY = this.scaleY(0, domain.min, domain.max, chart);
        const plotWidth = chart.width - chart.left - chart.right;
        const step = summaries.length ? plotWidth / summaries.length : plotWidth;
        const barWidth = Math.max(12, Math.min(24, step / 3.3));
        summaries.forEach((summary: any, index: number) => {
            const center = chart.left + step * index + step / 2;
            chart.labels.push({ x: center, text: summary.case?.shortLabel || summary.caseId });
            this.addBar(chart, center - barWidth - 3, barWidth, mode === "i3" ? summary.normalAvgI3 : summary.normalAvgDelta, domain, "#059669", `${summary.case?.shortLabel || summary.caseId} 정상군`);
            this.addBar(chart, center + 3, barWidth, mode === "i3" ? summary.patientAvgI3 : summary.patientAvgDelta, domain, "#e11d48", `${summary.case?.shortLabel || summary.caseId} 환자군`);
        });
        return chart;
    }

    private addBar(chart: any, x: number, width: number, value: any, domain: any, fill: string, title: string) {
        if (value === null || value === undefined || Number.isNaN(Number(value))) return;
        const zeroY = chart.zeroY;
        const valueY = this.scaleY(Number(value), domain.min, domain.max, chart);
        chart.bars.push({
            x,
            y: Math.min(zeroY, valueY),
            width,
            height: Math.max(2, Math.abs(zeroY - valueY)),
            value,
            fill,
            title: `${title}: ${this.format(value, 1)}`,
        });
    }

    private sampleCase(sample: any, caseId: string) {
        return sample?.cases?.find((row: any) => row.caseId === caseId) || null;
    }

    private groupKey(sample: any) {
        if (sample.sampleNumber && sample.replicate) return `${sample.sampleNumber}:${sample.replicate}`;
        return sample.subjectId || sample.group || sample.id;
    }

    private groupLabel(sample: any) {
        if (sample.sampleNumber && sample.replicate) return `${sample.sampleNumber} ${sample.replicate}`;
        return sample.subjectLabel || sample.subjectId || sample.name || sample.id;
    }

    private assayType(sample: any) {
        const source = `${sample.assay || ""} ${sample.group || ""} ${sample.name || ""} ${sample.id || ""}`.toLowerCase();
        if (source.indexOf("vehicle") >= 0) return "vehicle";
        if (source.indexOf("asc") >= 0) return "asc";
        return "";
    }

    private average(values: any[]) {
        const numericValues = values.map((value: any) => this.numberValue(value)).filter((value: any) => value !== null);
        if (!numericValues.length) return null;
        return this.finite(numericValues.reduce((sum: number, value: number) => sum + value, 0) / numericValues.length);
    }

    private numberValue(value: any) {
        if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
        return Number(value);
    }

    private finite(value: any) {
        const numeric = this.numberValue(value);
        if (numeric === null || !Number.isFinite(numeric)) return null;
        return Math.round(numeric * 1000) / 1000;
    }

    private chartDomain(values: any[], includeZero: boolean) {
        const numericValues = values.map((value: any) => this.numberValue(value)).filter((value: any) => value !== null) as number[];
        if (includeZero) numericValues.push(0);
        if (!numericValues.length) return { min: 0, max: 1 };
        let min = Math.min(...numericValues);
        let max = Math.max(...numericValues);
        if (min === max) {
            const pad = Math.max(Math.abs(max) * 0.1, 1);
            min -= pad;
            max += pad;
        } else {
            const pad = (max - min) * 0.14;
            min -= pad;
            max += pad;
        }
        return { min, max };
    }

    private buildTicks(min: number, max: number, count: number) {
        if (count <= 1) return [min];
        const step = (max - min) / (count - 1);
        return Array.from({ length: count }, (_: any, index: number) => min + step * index);
    }

    private scaleY(value: number, min: number, max: number, chart: any) {
        const height = chart.height - chart.top - chart.bottom;
        if (min === max) return chart.top + height / 2;
        return chart.top + (1 - ((value - min) / (max - min))) * height;
    }
}

export default PageI3Component;